// =============================================================================
// Admin Auth Store — Zustand store for admin authentication
// =============================================================================
// Admin users must have type='admin' in their JWT. The store enforces this
// by checking the user type after login/initialization.
// =============================================================================

import { create } from 'zustand';
import type { AuthUser, LoginRequest } from '@tkpod/shared';
import { api, startSessionKeeper, stopSessionKeeper } from './api';

export type AuthStatus = 'idle' | 'loading' | 'authenticated' | 'unauthenticated';

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  error: string | null;
}

interface AuthActions {
  initialize: () => Promise<void>;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: AuthUser) => void;
  clearError: () => void;
}

export type AuthStore = AuthState & AuthActions;

// Module-level promise to deduplicate concurrent initialize() calls (StrictMode, etc.)
let initPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthStore>((set, get) => ({
  status: 'idle',
  user: null,
  error: null,

  initialize: async () => {
    // Already resolved or in flight — skip
    if (get().status !== 'idle') return;
    if (initPromise) return initPromise;

    initPromise = (async () => {
      set({ status: 'loading', error: null });
      try {
        // noRetry: /auth/me returns 200 { user: null } for unauthenticated,
        // so 401 won't occur. noRetry is a safety net to prevent refresh loops
        // if the endpoint contract ever changes.
        const response = await api.get<{ user: AuthUser | null }>('/api/v1/auth/me', {
          noRetry: true,
        });

        // Admin panel requires admin-type users. A portal user with a valid
        // cookie on .transferkingdompod.com will get a user object back, but
        // type will be 'user' — treat as unauthenticated for admin panel.
        if (response.user && response.user.type === 'admin') {
          set({ status: 'authenticated', user: response.user, error: null });
          startSessionKeeper();
        } else {
          set({ status: 'unauthenticated', user: null, error: null });
        }
      } catch {
        // Network error or unexpected failure — treat as unauthenticated
        set({ status: 'unauthenticated', user: null, error: null });
      }
    })();

    return initPromise;
  },

  login: async (credentials: LoginRequest) => {
    set({ status: 'loading', error: null });
    try {
      const response = await api.post<{ user: AuthUser }>(
        '/api/v1/auth/admin/login',
        credentials,
        { noRetry: true },
      );

      // Double-check: backend should already enforce admin-only login,
      // but verify client-side for defense in depth.
      if (response.user.type !== 'admin') {
        set({ status: 'unauthenticated', user: null, error: 'Admin access required.' });
        return;
      }

      set({ status: 'authenticated', user: response.user, error: null });
      startSessionKeeper();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Login failed. Please try again.';
      set({ status: 'unauthenticated', user: null, error: message });
      throw err;
    }
  },

  logout: async () => {
    stopSessionKeeper();
    try {
      await api.post('/api/v1/auth/logout', undefined, { noRetry: true });
    } catch {
      // Non-critical
    }
    set({ status: 'unauthenticated', user: null, error: null });
  },

  setUser: (user: AuthUser) => {
    set({ user, status: 'authenticated' });
  },

  clearError: () => {
    set({ error: null });
  },
}));
